import React from 'react';
import { useSelector } from 'react-redux';
import ModalForm from '../../components/modal/ModalForm';
import CustomTable from '../../components/customtable/CustomTable';
import Loader from '../../components/loader/Loader';
import APPCONSTANTS from '../../constants/appConstants';
import { skDetailsViewLoadingSelector, skDetailsViewSelector } from '../../store/report/selectors';
import { ISKSsDetail, ISKSubVillage } from '../../store/report/types';
import styles from './SKDetails.module.scss';

interface ISKDetailsViewModalProps {
  show: boolean;
  onClose: () => void;
  period?: { startDate?: string; endDate?: string };
}

const statusBadge = (isActive: boolean) => (
  <span className={`${styles.statusBadge} ${isActive ? styles.active : styles.inactive}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

interface IVillageChipsProps {
  subVillages: ISKSubVillage[];
}

const VillageChips = ({ subVillages }: IVillageChipsProps): React.ReactElement => {
  if (!subVillages.length) {
    return <>{APPCONSTANTS.NOT_AVAILABLE}</>;
  }
  const showPerVillageCounts = subVillages.length > 1;
  return (
    <div className={styles.villageChips}>
      {subVillages.map((subVillage: ISKSubVillage) => (
        <span className={styles.villageChip} key={subVillage.subVillageId}>
          {subVillage.name}
          {showPerVillageCounts && (
            <span className={styles.villageChipCounts}>
              {` · HH ${subVillage.householdCount ?? 0} · Mem ${subVillage.memberCount ?? 0}`}
            </span>
          )}
        </span>
      ))}
    </div>
  );
};

const formatSsVillagesCell = (data: ISKSsDetail): React.ReactElement => (
  <VillageChips subVillages={data.subVillages || []} />
);

const formatSsStatusCell = (data: ISKSsDetail): React.ReactElement => statusBadge(Boolean(data.isActive));

const SKDetailsViewModal = ({ show, onClose, period }: ISKDetailsViewModalProps): React.ReactElement | null => {
  const view = useSelector(skDetailsViewSelector);
  const viewLoading = useSelector(skDetailsViewLoadingSelector);

  if (!show) {
    return null;
  }

  const summaryFields = view
    ? [
        { label: 'Name', value: view.skSummary?.name },
        { label: 'Username', value: view.skSummary?.username },
        { label: 'Phone Number', value: view.skSummary?.phoneNumber },
        { label: 'Gender', value: view.skSummary?.gender },
        { label: 'Union', value: view.skSummary?.unionNames },
        { label: 'District', value: view.skSummary?.district },
        { label: 'Upazila', value: view.skSummary?.upazila },
        { label: 'Status', value: view.skSummary?.isActive ? 'Active' : 'Inactive' }
      ]
    : [];

  const totals = view?.totals;
  const totalCards = totals
    ? [
        { label: 'SS', value: totals.ssCount },
        { label: 'Villages', value: totals.subVillageCount },
        { label: 'Households', value: totals.householdCount },
        { label: 'Members', value: totals.memberCount },
        { label: 'Services', value: totals.totalServiceCount }
      ]
    : [];
  const serviceBreakdown = totals
    ? [
        { label: 'ANC', value: totals.ancServiceCount },
        { label: 'PNC', value: totals.pncServiceCount },
        { label: 'Family Planning', value: totals.familyPlanningServiceCount },
        { label: 'NCD', value: totals.ncdServiceCount },
        { label: 'Child Visit', value: totals.childVisitServiceCount },
        { label: 'PW Registration', value: totals.pwRegistrationServiceCount },
        { label: 'Pregnancy Outcome', value: totals.pregnancyOutcomeServiceCount },
        { label: 'Eye Care', value: totals.eyeCareServiceCount },
        { label: 'Other', value: totals.otherServiceCount }
      ]
    : [];
  const hasPeriod = Boolean(period?.startDate || period?.endDate);

  const hasUnattributed = Boolean(
    totals && (totals.unattributedHouseholdCount > 0 || totals.unattributedMemberCount > 0)
  );

  const renderModalContent = () => (
    <div>
      {viewLoading || !view ? (
        <Loader isFullScreen={false} />
      ) : (
        <>
          <div className={styles.summaryGrid}>
            {summaryFields.map(({ label, value }) => (
              <div key={label}>
                <div className={styles.summaryLabel}>{label}</div>
                <div className={styles.summaryValue}>{value || APPCONSTANTS.NOT_AVAILABLE}</div>
              </div>
            ))}
          </div>
          {hasPeriod && (
            <div className={styles.periodNote}>
              {`Counts limited to ${period?.startDate || 'beginning'} → ${period?.endDate || 'today'}`}
            </div>
          )}
          <div className={styles.totalsStrip}>
            {totalCards.map(({ label, value }) => (
              <div className={styles.totalCard} key={label}>
                <div className={styles.totalValue}>{value ?? 0}</div>
                <div className={styles.totalLabel}>{label}</div>
              </div>
            ))}
          </div>
          <div className={styles.serviceBreakdown}>
            {serviceBreakdown.map(({ label, value }) => (
              <span className={styles.serviceBreakdownItem} key={label}>
                {`${label}: ${value ?? 0}`}
              </span>
            ))}
          </div>
          {hasUnattributed && (
            <div className={styles.unattributedNote}>
              {`${totals?.unattributedHouseholdCount} household(s) and ${totals?.unattributedMemberCount} ` +
                `member(s) are registered under this SK but could not be matched to a current active SS ` +
                `(e.g. removed or renamed SS).`}
            </div>
          )}
          <CustomTable
            rowData={view.ssList || []}
            columnsDef={[
              { id: 1, name: 'ssCode', label: 'SS ID' },
              { id: 2, name: 'name', label: 'Name' },
              {
                id: 3,
                name: 'subVillages',
                label: 'Villages',
                width: '180px',
                cellFormatter: formatSsVillagesCell
              },
              { id: 4, name: 'householdCount', label: 'Households', width: '90px' },
              { id: 5, name: 'memberCount', label: 'Members' },
              { id: 6, name: 'ancServiceCount', label: 'ANC' },
              { id: 7, name: 'pncServiceCount', label: 'PNC' },
              { id: 8, name: 'familyPlanningServiceCount', label: 'FP' },
              { id: 9, name: 'ncdServiceCount', label: 'NCD' },
              { id: 10, name: 'childVisitServiceCount', label: 'Child Visit' },
              { id: 11, name: 'pwRegistrationServiceCount', label: 'PW Reg' },
              { id: 12, name: 'pregnancyOutcomeServiceCount', label: 'Outcome' },
              { id: 13, name: 'eyeCareServiceCount', label: 'Eye Care' },
              { id: 14, name: 'otherServiceCount', label: 'Other' },
              { id: 15, name: 'totalServiceCount', label: 'Services' },
              {
                id: 16,
                name: 'isActive',
                label: 'Status',
                cellFormatter: formatSsStatusCell
              }
            ]}
            isEdit={false}
            isDelete={false}
            isView={false}
            isDownload={false}
          />
        </>
      )}
    </div>
  );

  const skName = view?.skSummary?.name;
  const modalTitle = skName ? `SK Coverage - ${skName}` : 'SK Coverage';

  return (
    <ModalForm
      show={show}
      title={modalTitle}
      size='modal-full'
      hasFooter={true}
      cancelText='Back to SK List'
      isScrollNeeded={true}
      handleCancel={onClose}
      handleFormSubmit={() => undefined}
      render={renderModalContent}
    />
  );
};

export default SKDetailsViewModal;
